import { useMemo, useState } from 'react';
import { PlusIcon, SearchXIcon, SlidersHorizontalIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { isAuthExpired, isForbidden } from '@/features/workspace-usage';
import { SECTION_HEADING } from '@/shared/components/sectionHeadingClass';
import { EmptyState } from '@/shared/components/EmptyState';
import { ListToolbar } from '@/shared/components/ListToolbar';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';

import { useCreateLlmProfile } from '../hooks/useCreateLlmProfile';
import { useDeleteLlmProfile } from '../hooks/useDeleteLlmProfile';
import { useLlmProfileRows } from '../hooks/useLlmProfileRows';
import { useLlmProfiles } from '../hooks/useLlmProfiles';
import { useModelPrices } from '../hooks/useModelPrices';
import { useUnpricedModelRuns } from '../hooks/useUnpricedModelRuns';
import { useUpdateLlmProfile } from '../hooks/useUpdateLlmProfile';
import {
  matchesProfileQuery,
  placeDeletedRows,
  profileCountsByModel,
  sortProfileRows,
  toLlmProfile,
  type LlmProfilesSort,
} from '../llmProfileRows';
import { buildModelPriceLookup } from '../modelPriceLookup';
import {
  parseProfilesSort,
  profilesSortParam,
  type ProfilesSearch,
} from '../searchParams';
import { generateProfileSlug } from '../slug';
import type { LlmProfile } from '../types';
import { buildUnpricedModelRunsLookup } from '../unpricedModelRunsLookup';
import { LlmProfileDeleteDialog } from './LlmProfileDeleteDialog';
import { LlmProfileFormSheet } from './LlmProfileFormSheet';
import type {
  LlmProfileFormMode,
  LlmProfileFormValues,
} from './LlmProfileFormSheet';
import { LlmProfilesTable } from './LlmProfilesTable';
import { LlmProfilesTableSkeleton } from './LlmProfilesTableSkeleton';

/**
 * The part of the address this section reads (spec 136 FR-011, data-model.md § 3) —
 * the column order and the filter, both namespaced because `/admin/harnesses` is
 * a route shared by three sections.
 *
 * Taken from `profilesSearchSchema`'s own type rather than restated, so the schema
 * stays the single definition of what the address can say — and from this feature's
 * own `searchParams.ts`, so naming the section's public prop type costs no dependency
 * on `organization-admin` at all.
 */
export type LlmProfilesView = ProfilesSearch;

export interface LlmProfilesSectionProps {
  view?: LlmProfilesView;
  onViewChange?: (next: LlmProfilesView) => void;
}

/**
 * The LLM Profiles section of the combined admin page (spec FR-001/FR-010).
 *
 * Mirrors `HarnessesSection`'s state machine (loading, load-failed with
 * retry, empty, loaded), scoped to a section rather than a whole page — with the
 * loaded branch now a flat grid (spec 136 US1) instead of the per-model grouping, and
 * a fifth branch beside the four: a filter that matches nothing is *not* an empty
 * registry, and the two must not read alike (US2 scenario 3).
 *
 * The order and the filter are **props**, read from the route's search params and
 * written back through `onViewChange` (FR-011) — deliberately never copied into local
 * state, which is how a bookmarked order and the arrow in the header start
 * disagreeing. Only the three things that are nobody else's business are local:
 * which profile's sheet is open, which row is expanded, and which model's price form
 * is open (data-model.md § 3).
 */
export function LlmProfilesSection({
  view = {},
  onViewChange,
}: LlmProfilesSectionProps) {
  const { items, deletedItems, isPending, loadFailed, error, refetch } =
    useLlmProfileRows();

  // The overview rows carry no `model_status`; the plain profile list does. Read
  // here and joined by slug so the table marker and the edit sheet use the
  // backend's verdict (spec 205 FR-007) rather than a client-side guess. A failed
  // or pending list just means no marker — it never blocks the grid.
  const { entries: profileEntries } = useLlmProfiles();
  const modelStatusBySlug = useMemo(
    () =>
      new Map(
        (profileEntries ?? []).map((profile) => [
          profile.slug,
          profile.model_status,
        ]),
      ),
    [profileEntries],
  );

  // A placeholder only once the wait is worth remarking on (`079` FR-017).
  const showPlaceholder = usePendingDisplay(isPending);

  // Fetched once here, alongside the profiles themselves, rather than inside the
  // table — the lookup is a page-level concern the table only consumes (spec 092
  // research.md R2).
  const modelPrices = useModelPrices();
  const priceLookup = useMemo(
    () =>
      modelPrices.data
        ? buildModelPriceLookup(modelPrices.data.prices)
        : undefined,
    [modelPrices.data],
  );

  // How much of the current period ran unpriced, per model — named beside the "No
  // price" chip so a reader deciding whether a gap is worth acting on now sees its
  // size in the same glance (spec 092 follow-up).
  const unpricedModelRuns = useUnpricedModelRuns();
  const unpricedRunsLookup = useMemo(
    () =>
      unpricedModelRuns.data
        ? buildUnpricedModelRunsLookup(unpricedModelRuns.data.models)
        : undefined,
    [unpricedModelRuns.data],
  );

  /** The profile whose sheet is open — drives `aria-selected` *and* the sheet. */
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  /** At most one expanded row (FR-001a): a slug, not a set. */
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null);
  /**
   * The model whose inline price form is open, keyed by **model key** rather than by
   * slug — preserving spec 092's one-open-form-at-a-time invariant now that the
   * per-model groups that used to carry it are gone (FR-016).
   */
  const [pricingModel, setPricingModel] = useState<string | null>(null);
  const [dialog, setDialog] = useState<LlmProfileFormMode | null>(null);
  const [pendingDelete, setPendingDelete] = useState<LlmProfile | null>(null);

  const create = useCreateLlmProfile();
  const update = useUpdateLlmProfile();
  const remove = useDeleteLlmProfile();

  const saving = create.isPending || update.isPending;
  const saveError = dialog?.kind === 'edit' ? update.error : create.error;

  // Memoized, not a bare `items ?? []`: the fallback would be a fresh array on every
  // render and both memos below take it as a dependency, so the counts and the sort
  // would recompute for every keystroke in the filter field.
  const all = useMemo(() => items ?? [], [items]);
  const deleted = useMemo(() => deletedItems ?? [], [deletedItems]);

  const sort = parseProfilesSort(view.profilesSort);
  const query = view.profilesQ ?? '';

  const updateView = (next: LlmProfilesView) => {
    onViewChange?.({ ...view, ...next });
  };

  // Over the **unfiltered** response: "is this the only profile for its model" is a
  // fact about the registry, not about what the reader searched for (FR-009). Feeds
  // both badges and `SetDefaultProfileControl`'s `siblingCount`.
  const counts = useMemo(() => profileCountsByModel(all), [all]);

  const rows = useMemo(
    () =>
      sortProfileRows(
        all.filter((row) => matchesProfileQuery(row, query)),
        sort,
      ),
    [all, query, sort],
  );

  // The deleted profiles with usage, shown among the rows but never counted, filtered
  // on or acted on (spec 258 FR-004).
  const entries = useMemo(
    () => placeDeletedRows(rows, deleted, query, sort),
    [rows, deleted, query, sort],
  );

  /**
   * The open price form, but only while the row that hosts it is on screen (R-F33).
   *
   * The form renders inside the expanded detail row, so a filter that drops that row
   * left `pricingModel` set with no form rendered anywhere — and `RateCell` hides
   * `Add rate`/`Change rate` for every row on that model while it is set, so the rate
   * affordance silently vanished for the whole model. Derived rather than cleared in an
   * effect: "the form is open" is a fact about the rows actually displayed.
   */
  const openPricingModel =
    pricingModel !== null &&
    rows.some((row) => row.slug === expandedSlug && row.model === pricingModel)
      ? pricingModel
      : null;

  function openCreate() {
    create.reset();
    update.reset();
    setDialog({ kind: 'create' });
  }

  function openEdit(entry: LlmProfile) {
    create.reset();
    update.reset();
    setDialog({ kind: 'edit', entry });
  }

  function openDelete(entry: LlmProfile) {
    remove.reset();
    setPendingDelete(entry);
  }

  function handleSubmit(input: LlmProfileFormValues) {
    if (dialog?.kind === 'edit') {
      update.mutate(
        { slug: dialog.entry.slug, body: input },
        {
          onSuccess: () => {
            setDialog(null);
            toast.success(`Saved ${input.name}.`);
          },
        },
      );
      return;
    }

    create.mutate(
      {
        ...input,
        slug: generateProfileSlug(
          input.name,
          all.map((entry) => entry.slug),
        ),
      },
      {
        onSuccess: () => {
          setDialog(null);
          toast.success(`Added ${input.name}.`);
        },
      },
    );
  }

  function handleDelete() {
    if (!pendingDelete) return;
    const { slug, name } = pendingDelete;
    remove.mutate(slug, {
      onSuccess: () => {
        setPendingDelete(null);
        // The expansion **closes** rather than transferring to whichever row slides
        // up into the gap: a detail panel that stays open against a different
        // profile's data is the one outcome a reader cannot be expected to notice.
        if (expandedSlug === slug) setExpandedSlug(null);
        if (selectedSlug === slug) setSelectedSlug(null);
        toast.success(`Deleted ${name}.`);
      },
    });
  }

  return (
    <div
      data-testid="llm-profiles-region"
      className="flex flex-col gap-3"
      aria-labelledby="llm-profiles-heading"
    >
      <div className="flex items-center justify-between max-md:flex-col max-md:items-stretch max-md:gap-3">
        <div className="flex flex-col gap-1">
          <h2 id="llm-profiles-heading" className={SECTION_HEADING}>
            LLM Profiles
          </h2>
          <p className="text-muted-foreground max-w-prose text-sm">
            Models come first — pick one, then add a profile for it. Each
            profile has its own effort level, and one profile per model can be
            set as the default.
          </p>
        </div>
        <Button size="sm" className="max-md:w-full" onClick={openCreate}>
          <PlusIcon aria-hidden="true" />
          New profile
        </Button>
      </div>

      {isPending ? (
        showPlaceholder && <LlmProfilesTableSkeleton />
      ) : loadFailed ? (
        <EmptyState
          icon={SlidersHorizontalIcon}
          description={
            // A refusal is the expected answer for a member rather than an admin, and
            // saying so is more use than "could not be loaded" — the same split
            // `AdminLimitsPage` makes, switching on the error's kind rather than on
            // its message text. The third case is an expired session, where "Try
            // again" is the wrong instruction entirely (R-F35).
            isForbidden(error)
              ? 'Only an organization administrator can see LLM profiles.'
              : isAuthExpired(error)
                ? 'Your session has expired. Reload the page to sign in again.'
                : 'The LLM profiles could not be loaded.'
          }
          action={
            isAuthExpired(error) ? (
              // Not a refetch: the session is gone, so retrying the read answers 401
              // again. A reload goes through `RequireAuth`, which signs in.
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  window.location.reload();
                }}
              >
                Reload
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refetch()}
              >
                Try again
              </Button>
            )
          }
        />
      ) : all.length === 0 && deleted.length === 0 ? (
        <EmptyState
          icon={SlidersHorizontalIcon}
          description="No profiles yet. Add one to give a workflow step a model and the settings it runs under."
        />
      ) : (
        <>
          <ListToolbar
            label="Filter LLM profiles"
            placeholder="Filter by name, model or provider"
            query={query}
            onQueryChange={(next) => {
              // An empty filter is **absent** from the address rather than written
              // into it as `profilesQ=`, so a shared link says only what the reader
              // actually changed.
              updateView({ profilesQ: next === '' ? undefined : next });
            }}
            shown={rows.length}
            total={all.length}
            noun={['profile', 'profiles']}
          />
          {entries.length === 0 ? (
            // Distinct from the empty state above, and deliberately so (US2 scenario
            // 3): there are profiles here, just not these, so this offers a way back
            // to all of them rather than an invitation to add a first one.
            <EmptyState
              icon={SearchXIcon}
              description={`No profile matches “${query.trim()}”.`}
              action={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    updateView({ profilesQ: undefined });
                  }}
                >
                  Clear filter
                </Button>
              }
            />
          ) : (
            <LlmProfilesTable
              rows={entries}
              modelStatusBySlug={modelStatusBySlug}
              counts={counts}
              selectedSlug={selectedSlug ?? undefined}
              onSelect={(slug) => {
                setSelectedSlug(slug);
                const row = all.find((candidate) => candidate.slug === slug);
                // `toLlmProfile` is where the two endpoint shapes meet (FR-018a):
                // the sheet reads `ai_provider_id`, which an overview row carries as
                // a nested object, and widening the sheet's props instead would
                // change a file SC-005 is judged by.
                if (row)
                  openEdit(toLlmProfile(row, modelStatusBySlug.get(row.slug)));
              }}
              expandedSlug={expandedSlug}
              onToggleExpand={(slug) => {
                const collapsing = expandedSlug === slug;
                setExpandedSlug(collapsing ? null : slug);
                // Collapsing the row that hosts the open price form closes the form
                // with it (R-F33), so re-expanding that row later does not reopen a
                // form the reader had already dismissed by collapsing it. Read from
                // `expandedSlug` rather than from an updater, which must stay pure.
                if (collapsing && pricingModel !== null) setPricingModel(null);
              }}
              sort={sort}
              onSortChange={(next: LlmProfilesSort | undefined) => {
                updateView({
                  profilesSort: next ? profilesSortParam(next) : undefined,
                });
              }}
              priceLookup={priceLookup}
              // The failure, not just the absence: both lookups stay `undefined`
              // forever on a 403 or a 500, so without these the Rate column was
              // permanently blank with nothing to read (R-F4).
              priceLookupFailed={modelPrices.isError}
              unpricedRunsLookup={unpricedRunsLookup}
              unpricedRunsLookupFailed={unpricedModelRuns.isError}
              pricingModel={openPricingModel}
              onPriceFormOpen={setPricingModel}
              onEdit={openEdit}
              onDelete={openDelete}
            />
          )}
        </>
      )}

      {dialog ? (
        <LlmProfileFormSheet
          key={dialog.kind === 'edit' ? `edit:${dialog.entry.slug}` : 'create'}
          mode={dialog}
          open
          isPending={saving}
          error={saveError}
          onSubmit={handleSubmit}
          onClearError={dialog.kind === 'edit' ? update.reset : create.reset}
          onCancel={() => {
            setDialog(null);
            // The selection goes with the sheet it drove: a row left reading as
            // selected with nothing open claims a state the surface is not in.
            setSelectedSlug(null);
          }}
        />
      ) : null}

      {pendingDelete ? (
        <LlmProfileDeleteDialog
          name={pendingDelete.name}
          open
          isPending={remove.isPending}
          error={remove.error}
          onConfirm={handleDelete}
          onCancel={() => {
            setPendingDelete(null);
            remove.reset();
          }}
        />
      ) : null}
    </div>
  );
}
